<template>
  <HomeInicio />

  <div class="dashboard-container">
    <div class="dashboard-header">
      <h2 class="titulo-dashboard">Resumen del Evento</h2>
      <div class="dashboard-actions">
        <button class="refresh-btn" @click="refreshData" title="Actualizar datos">
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>Cargando datos del evento...</p>
    </div>

    <div v-else-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="cargarDatos">Reintentar</button>
    </div>

    <div v-else class="dashboard-grid">
      <div 
        v-for="(card, index) in cards" 
        :key="index" 
        class="dashboard-card" 
        @click="goTo(card.route)"
      >
        <div class="card-icon" :class="card.color">
          <i :class="card.icon"></i>
        </div>
        <div class="card-content">
          <h3 class="card-title">{{ card.title }}</h3>
          <p class="card-value">{{ card.value }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import HomeInicio from "@/components/HomeInicio.vue"

export default {
  name: 'EventDashboard',
  components: {
    HomeInicio
  },
  setup() {
    const router = useRouter()
    const isLoading = ref(true)
    const error = ref(null)

    const totalInvitados = ref(0)
    const totalConfirmados = ref(0)
    const totalRechazados = ref(0)
    const totalPases = ref(0)

    const idFestejada = localStorage.getItem('id_festejada')

    const cards = computed(() => [
      {
        icon: 'fas fa-user-friends',
        title: 'Total Invitados',
        value: totalInvitados.value,
        color: 'blue'
      },
      {
        icon: 'fas fa-ticket-alt',
        title: 'Total de Pases',
        value: totalPases.value,
        color: 'gold'
      },
      {
        icon: 'fas fa-check-circle',
        title: 'Pases Confirmados',
        value: totalConfirmados.value,
        color: 'green'
      },
      {
        icon: 'fas fa-times-circle',
        title: 'Invitados Rechazaron',
        value: totalRechazados.value,
        color: 'red'
      }
    ])

    const cargarDatos = async () => {
      try {
        isLoading.value = true
        error.value = null

        if (!idFestejada) {
          throw new Error('No se encontró el ID de la festejada')
        }

        const query = `?id_festejada=${idFestejada}`
        const endpoints = [
          '/api2/total_invitados',
          '/api2/total_confirmados',
          '/api2/total_rechazados',
          '/api2/total_pases'
        ]

        const responses = await Promise.all(
          endpoints.map(endpoint => axios.get(`${endpoint}${query}`))
        )

        // Asignación directa sin animación
        totalInvitados.value = parseInt(responses[0]?.data?.total) || 0
        totalConfirmados.value = parseInt(responses[1]?.data?.total) || 0
        totalRechazados.value = parseInt(responses[2]?.data?.total) || 0
        totalPases.value = parseInt(responses[3]?.data?.total) || 0

      } catch (err) {
        error.value = 'Error al cargar los datos del evento'
        console.error('Error:', err)
      } finally {
        isLoading.value = false
      }
    }

    const refreshData = async () => {
      await cargarDatos()
    }

    const goTo = (ruta) => {
      const routes = {
        invitados: 'AgregarInvitado',
        confirmados: 'confirmados',
        rechazados: 'rechazados',
        pases: 'dashboard'
      }
      if (routes[ruta]) router.push({ name: routes[ruta] })
    }

    onMounted(async () => {
      await cargarDatos()
    })

    return {
      isLoading,
      error,
      cards,
      goTo,
      HomeInicio,
      refreshData,
      cargarDatos
    }
  }
}
</script>



<style scoped>
/* Estilos base */
.dashboard-container {
  display: flex;
  flex-direction: column;
  align-items: center;     /* Centrado horizontal */
  min-height: 100vh;
  margin-top:90px;
  padding: 2rem 1rem;
  box-sizing: border-box;
  background-color: #f9fafb;
}



/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.titulo-dashboard {
  font-size: 1.5rem;
  font-family: 'Playfair Display', serif;
  color: #2d3748;
  margin: 0.5rem 0;
  font-weight: 600;
  flex: 1;
  min-width: 200px;
}

.dashboard-actions {
  display: flex;
  gap: 0.5rem;
}

.refresh-btn {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4a5568;
}

.refresh-btn:hover {
  background: #f7fafc;
  transform: rotate(180deg);
  color: #2d3748;
}

/* Grid de tarjetas */
.dashboard-grid {
  width: 100%;
  max-width: 600px; /* o 90% si prefieres fluido */
  margin: 0 auto;
}


@media (min-width: 480px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 768px) {
  .dashboard-grid {
    gap: 1.5rem;
  }
}

/* Tarjetas */
.dashboard-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0; /* Previene desbordamiento */
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.card-icon {
  min-width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

@media (min-width: 480px) {
  .card-icon {
    min-width: 54px;
    height: 54px;
    font-size: 1.5rem;
  }
}

.card-icon.blue {
  background: #ebf8ff;
  color: #3182ce;
}

.card-icon.green {
  background: #f0fff4;
  color: #38a169;
}

.card-icon.red {
  background: #fff5f5;
  color: #e53e3e;
}

.card-icon.gold {
  background: #fffaf0;
  color: #d69e2e;
}

.card-content {
  flex: 1;
  text-align: left;
  min-width: 0; /* Previene desbordamiento */
  overflow: hidden; /* Previene desbordamiento */
}

.card-title {
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  color: #4a5568;
  margin: 0 0 0.25rem 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 480px) {
  .card-title {
    font-size: 1rem;
  }
}

.card-value {
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  color: #2d3748;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 480px) {
  .card-value {
    font-size: 1.5rem;
  }
}

/* Estados */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 1rem;
}

.loading-spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3182ce;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  color: #e53e3e;
}

.error-message i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.error-message p {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
}

.retry-btn {
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.875rem;
}

.retry-btn:hover {
  background: #c53030;
}
</style>